import type {
  ActiveWindow,
  AdminDashboard,
  DeliveryChannel,
  DeliveryStat,
  DeliveryStatus,
  Language,
  StatsPeriod,
} from "@/types/api";
import { ACTIVE_WINDOWS, DELIVERY_CHANNELS, LANGUAGES, STATS_PERIODS } from "@/types/api";

import { ADMIN_TIME_ZONE, percent } from "./format";
import { readEnum } from "./url-params";

// ─── Sanalar ─────────────────────────────────────────────────────────────────

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isDate = (value: string | null | undefined): value is string =>
  !!value && DATE_RE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

/** Bugungi sana Toshkentda — backend ham kunlarni shu zonada kesadi. */
export function todayIn(timeZone = ADMIN_TIME_ZONE, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

const parse = (date: string) => new Date(`${date}T00:00:00Z`);
const iso = (date: Date) => date.toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  const value = parse(date);
  value.setUTCDate(value.getUTCDate() + days);
  return iso(value);
}

export function addMonths(date: string, months: number): string {
  const value = parse(date);
  value.setUTCDate(1);
  value.setUTCMonth(value.getUTCMonth() + months);
  return iso(value);
}

/** Dushanba — hafta boshi (backend `startOfWeek` bilan bir xil). */
export function startOfWeek(date: string): string {
  const day = parse(date).getUTCDay();
  return addDays(date, day === 0 ? -6 : 1 - day);
}

// ─── Dashboard filtrlari ─────────────────────────────────────────────────────

export const DASHBOARD_PRESETS = ["today", "7d", "30d", "90d", "year"] as const;
export type DashboardPreset = (typeof DASHBOARD_PRESETS)[number];

const PRESET_DAYS: Record<DashboardPreset, number> = {
  today: 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
  year: 365,
};

export function presetRange(preset: DashboardPreset, today: string) {
  return { from: addDays(today, 1 - PRESET_DAYS[preset]), to: today };
}

/** Oraliq tayyor variantlardan biriga to'g'ri keladimi — tugma faol ko'rinsin. */
export function matchPreset(from: string, to: string, today: string): DashboardPreset | null {
  return (
    DASHBOARD_PRESETS.find(preset => {
      const range = presetRange(preset, today);
      return range.from === from && range.to === to;
    }) ?? null
  );
}

export interface DashboardParams {
  from: string;
  to: string;
  language?: Language;
  channel?: DeliveryChannel;
  activeDays: ActiveWindow;
}

/**
 * Manzildan filtrlar. Sana yo'q yoki buzuq — oxirgi 30 kun (backend
 * standarti bilan bir xil), teskari oraliq — almashtiriladi.
 */
export function readDashboardParams(params: URLSearchParams, today: string): DashboardParams {
  const fallback = presetRange("30d", today);
  const rawFrom = params.get("from");
  const rawTo = params.get("to");
  let from = isDate(rawFrom) ? rawFrom : fallback.from;
  let to = isDate(rawTo) ? rawTo : fallback.to;
  if (from > to) [from, to] = [to, from];
  const active = Number(params.get("active"));
  return {
    from,
    to,
    language: readEnum(params, "lang", LANGUAGES),
    channel: readEnum(params, "channel", DELIVERY_CHANNELS),
    activeDays: (ACTIVE_WINDOWS as readonly number[]).includes(active)
      ? (active as ActiveWindow)
      : 7,
  };
}

// ─── Ko'rsatkichlar ──────────────────────────────────────────────────────────

export interface Delta {
  diff: number;
  /** Oldingi davrga nisbatan foiz; oldingisi nol bo'lsa — `null` (cheksiz o'sish). */
  percent: number | null;
  trend: "up" | "down" | "flat";
}

export function delta(current: number, previous: number): Delta {
  const diff = current - previous;
  return {
    diff,
    percent: previous === 0 ? null : Math.round((diff / previous) * 100),
    trend: diff > 0 ? "up" : diff < 0 ? "down" : "flat",
  };
}

/** Ustun balandligi 0..100; nol bo'lmagan qiymat ko'rinadigan darajada (≥ 4). */
export function barHeights(values: number[]): number[] {
  const max = Math.max(0, ...values);
  return values.map(value => (value <= 0 ? 0 : Math.max(4, Math.round((value / max) * 100))));
}

export function splitMinutes(total: number): { hours: number; minutes: number } {
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

export interface FunnelStep {
  key: keyof AdminDashboard["funnel"];
  value: number;
  /** Birinchi bosqichga nisbatan. */
  share: number;
}

export function funnelSteps(funnel: AdminDashboard["funnel"]): FunnelStep[] {
  const keys = ["registered", "createdTask", "completedTask", "wroteReview"] as const;
  return keys.map(key => ({
    key,
    value: funnel[key],
    share: percent(funnel[key], funnel.registered),
  }));
}

// ─── Yetkazishlar ────────────────────────────────────────────────────────────

export interface ChannelSummary {
  channel: DeliveryChannel;
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  /** Yakunlangan urinishlar ichida muvaffaqiyatlisi, foiz. */
  successRate: number;
}

/** `READ` ham yetkazilgan: foydalanuvchi uni ko'rgan. */
const DELIVERED: DeliveryStatus[] = ["SENT", "READ"];

/**
 * Kanal bo'yicha yetkazish xulosasi. `SKIPPED` (foydalanuvchi o'chirgan)
 * foizga kirmaydi — bu xato emas, tanlov.
 */
export function summarizeDeliveries(
  stats: DeliveryStat[],
  channels: DeliveryChannel[] = ["TELEGRAM", "WEB"],
): ChannelSummary[] {
  return channels.map(channel => {
    const rows = stats.filter(row => row.channel === channel);
    const sum = (statuses: DeliveryStatus[]) =>
      rows.filter(row => statuses.includes(row.status)).reduce((acc, row) => acc + row.count, 0);
    const delivered = sum(DELIVERED);
    const failed = sum(["FAILED"]);
    return {
      channel,
      total: rows.reduce((acc, row) => acc + row.count, 0),
      delivered,
      failed,
      pending: sum(["PENDING"]),
      successRate: percent(delivered, delivered + failed),
    };
  });
}

// ─── Foydalanuvchi davri ─────────────────────────────────────────────────────

export function readPeriod(params: URLSearchParams, today: string) {
  const date = params.get("date");
  return {
    period: readEnum(params, "period", STATS_PERIODS) ?? ("week" as StatsPeriod),
    date: isDate(date) ? date : today,
  };
}

/** Oldingi / keyingi davr: kun ±1, hafta ±7, oy ±1 oy, yil ±12 oy. */
export function shiftPeriod(period: StatsPeriod, date: string, direction: 1 | -1): string {
  switch (period) {
    case "day":
      return addDays(date, direction);
    case "week":
      return addDays(startOfWeek(date), 7 * direction);
    case "month":
      return addMonths(date, direction);
    case "year":
      return addMonths(date, 12 * direction);
  }
}

// ─── Faollik kalendari ───────────────────────────────────────────────────────

export interface CalendarCell {
  date: string;
  count: number;
  /** 0 — bo'sh, 1..4 — to'q-ochlik (kvartil bo'yicha). */
  level: 0 | 1 | 2 | 3 | 4;
  future: boolean;
}

/**
 * GitHub uslubidagi kalendar: ustunlar — haftalar (dushanbadan), oxirgisi
 * bugungi hafta. Daraja eng faol kunga nisbatan: bitta "rekord" kun
 * qolganlarini yo'qotib yubormasin, shuning uchun to'rt teng qadam.
 */
export function calendarWeeks(
  entries: { date: string; count: number }[],
  today: string,
  weeks = 53,
): CalendarCell[][] {
  const counts = new Map(entries.map(entry => [entry.date, entry.count]));
  const max = Math.max(0, ...entries.map(entry => entry.count));
  const first = addDays(startOfWeek(today), -7 * (weeks - 1));
  return Array.from({ length: weeks }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => {
      const date = addDays(first, week * 7 + day);
      const count = counts.get(date) ?? 0;
      const level =
        count === 0 || max === 0 ? 0 : (Math.min(4, Math.ceil((count / max) * 4)) as 1 | 2 | 3 | 4);
      return { date, count, level, future: date > today };
    }),
  );
}
